"use client"

import { useId } from "react"

import { Label } from "@/components/ui/label"
import { useSelfLearningQuery } from "@/hooks/queries/self-learning"
import { usePermissions } from "@/hooks/use-permissions"
import { MESSAGING_AGENTS_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { actsAsSuggestOnly } from "@/lib/conversation-quality/self-learning"
import {
  NO_OWN_INSTRUCTIONS_LINE,
  SELF_LEARNING_COPY,
  SELF_LEARNING_LABEL,
  agentsLearningLine,
  instructionsFull,
  instructionsRoomLabel,
  instructionsRoomNote,
  type InstructionsRoom,
} from "@/lib/conversation-quality/self-learning-display"
import { cn } from "@/lib/utils"
import type { SelfLearningSummaryResult } from "@/server-actions/conversation-quality"

import { SelfLearningArticlesSwitch } from "./self-learning-articles-switch"
import { SelfLearningModeSelect } from "./self-learning-mode-select"

type Learners = SelfLearningSummaryResult["learners"]
type ListedAgent = Extract<Learners, { scope: "agents" }>["agents"][number]

/**
 * The same control wherever the page shows Self-learning: one agent in scope gets its mode, the Automatic option and
 * the room left in its instructions; several get a row each.
 */
export function LearnersSetting({ learners }: { learners: Learners }) {
  const { can } = usePermissions()
  const canView = can(MESSAGING_AGENTS_PERMISSIONS.VIEW)
  const canManage = can(MESSAGING_AGENTS_PERMISSIONS.MANAGE)

  switch (learners.scope) {
    case "agent": {
      const { agent } = learners
      return (
        <div className="flex min-w-0 flex-col gap-3">
          <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
            <div className="min-w-0 flex-1 basis-64 space-y-2">
              <ModeRow agent={agent} canView={canView} canManage={canManage} />
              {canView ? (
                <ModeDescription agentId={agent.id} fallback={agent.mode} />
              ) : (
                <p className="text-sm text-muted-foreground">{SELF_LEARNING_COPY[agent.mode].description}</p>
              )}
            </div>
            <RoomMeter room={agent.instructions} className="flex-1 basis-56" />
          </div>
          {canView && <SelfLearningArticlesSwitch agentId={agent.id} canManage={canManage} />}
          {actsAsSuggestOnly(agent.mode, agent.hasOwnInstructions) && (
            <p className="text-xs font-medium">{NO_OWN_INSTRUCTIONS_LINE}</p>
          )}
        </div>
      )
    }
    case "agents":
      return (
        <div className="min-w-0 space-y-2">
          <p className="text-sm text-muted-foreground">{agentsLearningLine(learners.learning, learners.total)}</p>
          {learners.agents.length > 0 && (
            <ul className="divide-y rounded-md border">
              {learners.agents.map((agent) => (
                <li key={agent.id} className="px-3 py-2">
                  <ModeRow agent={agent} canView={canView} canManage={canManage} nameAsLabel />
                </li>
              ))}
            </ul>
          )}
        </div>
      )
    default: {
      const exhaustive: never = learners
      return exhaustive
    }
  }
}

function ModeRow({
  agent,
  canView,
  canManage,
  nameAsLabel = false,
}: {
  agent: ListedAgent;
  canView: boolean;
  canManage: boolean;
  /** In a list of agents the select is named by its agent. */
  nameAsLabel?: boolean;
}) {
  const selectId = useId()
  const mode = SELF_LEARNING_COPY[agent.mode].label
  if (!canView) return <p className="text-sm">{`${nameAsLabel ? agent.name : SELF_LEARNING_LABEL}: ${mode}`}</p>

  return (
    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
      <Label htmlFor={selectId} className={cn("min-w-0 break-words", nameAsLabel ? "font-normal" : "font-medium")}>
        {nameAsLabel ? agent.name : "Mode"}
      </Label>
      <SelfLearningModeSelect
        agentId={agent.id}
        agentName={agent.name}
        canManage={canManage}
        id={selectId}
        triggerClassName="h-9 w-full sm:w-52"
        fallback={<span className="text-sm">{mode}</span>}
      />
    </div>
  )
}

/** Read from the agent's own setting, so it follows a change made in the select beside it. */
function ModeDescription({ agentId, fallback }: { agentId: string; fallback: ListedAgent["mode"] }) {
  const { data } = useSelfLearningQuery(agentId)
  return <p className="text-sm text-muted-foreground">{SELF_LEARNING_COPY[data?.mode ?? fallback].description}</p>
}

function RoomMeter({ room, className }: { room: InstructionsRoom; className?: string }) {
  const full = instructionsFull(room)
  const used = Math.min(100, (room.length / room.limit) * 100)

  return (
    <div className={cn("min-w-0 space-y-1.5", className)}>
      <div className="flex flex-wrap justify-between gap-x-3 text-sm">
        <span className="text-muted-foreground">Instructions</span>
        <span className="tabular-nums">{instructionsRoomLabel(room)}</span>
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-full bg-muted"
        role="meter"
        aria-label="Instructions used"
        aria-valuemin={0}
        aria-valuemax={room.limit}
        aria-valuenow={room.length}
      >
        <div className={cn("h-full", full ? "bg-amber-500" : "bg-violet-600")} style={{ width: `${used}%` }} />
      </div>
      <p className={cn("text-xs", full ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground")}>
        {instructionsRoomNote(room)}
      </p>
    </div>
  )
}
